export const MISSION_SPINE_EVENT_NAME = 'ownyou:mission-spine';
export const MISSION_SPINE_CONSOLE_PREFIX = '[MissionSpine]';
export const MISSION_SPINE_DEBUG_GLOBAL = '__OWNYOU_MISSION_SPINE__';

export type MissionSpineHop =
  | 'baseline'
  | 'source_write'
  | 'ikigai_detection_start'
  | 'ikigai_route_plan'
  | 'ikigai_source_input'
  | 'ikigai_opportunity_write'
  | 'bridge_received'
  | 'worker_notify_sent'
  | 'worker_notify_received'
  | 'worker_process_opportunities'
  | 'missions_generated'
  | 'trigger_persist'
  | 'use_missions_result'
  | 'today_visible_missions'
  | 'mission_detail_render'
  | 'lifecycle_transition'
  | 'evidence_render'
  | 'today_chip_filter'
  | 'triage_render'
  | 'triage_action'
  | 'action_modal'
  | 'mission_reaction_write'
  | 'feedback_ranking_result'
  | 'inbox_event_append'
  | 'inbox_feed_read'
  | 'inbox_mark_read'
  | 'tracking_request_render';

export type MissionSpineStatus =
  | 'baseline'
  | 'start'
  | 'success'
  | 'skipped'
  | 'blocked'
  | 'error';

export interface MissionSpineRouteRef {
  routeId?: string;
  provider?: string;
  model?: string;
  selectionSource?: string;
}

export interface MissionSpineMarker {
  type: 'mission_spine_marker';
  version: 1;
  runId: string;
  timestamp: number;
  hop: MissionSpineHop;
  status: MissionSpineStatus;
  namespace?: string;
  sourceId?: string;
  reasonCode?: string;
  counts?: Record<string, number>;
  route?: MissionSpineRouteRef;
}

export interface MissionSpineMarkerInput {
  runId?: string;
  timestamp?: number;
  hop: MissionSpineHop;
  status: MissionSpineStatus;
  namespace?: string;
  sourceId?: string;
  reasonCode?: string;
  counts?: Record<string, number | undefined | null>;
  route?: MissionSpineRouteRef;
}

export interface MissionSpineDebugState {
  markers: MissionSpineMarker[];
  summarize: () => {
    markerCount: number;
    hops: string[];
    countsByHop: Record<string, number>;
    latest?: MissionSpineMarker;
  };
  reset: () => void;
}

declare global {
  interface Window {
    __OWNYOU_MISSION_SPINE__?: MissionSpineDebugState;
  }
}

const MAX_MARKERS = 500;
const MAX_TOKEN_LENGTH = 96;
const UNSAFE_DIAGNOSTIC_TOKEN_TERMS = [
  'body',
  'credential',
  'completion',
  'prompt',
  'provider_response',
  'raw',
  'secret',
  'token',
  'wallet',
] as const;
let baselineEmitted = false;

function sanitizeToken(value: string | undefined, fallback?: string): string | undefined {
  if (!value) return fallback;
  const clean = value
    .replace(/[^\w:./@-]+/g, '_')
    .slice(0, MAX_TOKEN_LENGTH);
  return clean || fallback;
}

function hasUnsafeDiagnosticTerm(value: string): boolean {
  const normalized = value.toLowerCase().replace(/[^\w]+/g, '_');
  return UNSAFE_DIAGNOSTIC_TOKEN_TERMS.some(term => normalized.includes(term));
}

function sanitizeDiagnosticToken(value: string | undefined, fallback?: string): string | undefined {
  const clean = sanitizeToken(value);
  if (clean && !hasUnsafeDiagnosticTerm(clean)) return clean;
  if (fallback && !hasUnsafeDiagnosticTerm(fallback)) return fallback;
  return undefined;
}

function sanitizeCounts(counts?: MissionSpineMarkerInput['counts']): Record<string, number> | undefined {
  if (!counts) return undefined;

  const safe: Record<string, number> = {};
  for (const [key, rawValue] of Object.entries(counts)) {
    if (typeof rawValue !== 'number' || !Number.isFinite(rawValue)) continue;
    const safeKey = sanitizeDiagnosticToken(key);
    if (!safeKey) continue;
    safe[safeKey] = Math.max(0, Math.trunc(rawValue));
  }

  return Object.keys(safe).length > 0 ? safe : undefined;
}

function sanitizeRoute(route?: MissionSpineRouteRef): MissionSpineRouteRef | undefined {
  if (!route) return undefined;

  const safe: MissionSpineRouteRef = {};
  const routeId = sanitizeToken(route.routeId);
  const provider = sanitizeToken(route.provider);
  const model = sanitizeToken(route.model);
  const selectionSource = sanitizeToken(route.selectionSource);
  if (routeId) safe.routeId = routeId;
  if (provider) safe.provider = provider;
  if (model) safe.model = model;
  if (selectionSource) safe.selectionSource = selectionSource;
  return Object.keys(safe).length > 0 ? safe : undefined;
}

function getBrowserWindow(): Window | null {
  if (typeof window === 'undefined') return null;
  return window;
}

export function createMissionSpineRunId(prefix = 'mission-spine'): string {
  return `${sanitizeToken(prefix, 'mission-spine')}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createMissionSpineMarker(input: MissionSpineMarkerInput): MissionSpineMarker {
  const marker: MissionSpineMarker = {
    type: 'mission_spine_marker',
    version: 1,
    runId: sanitizeDiagnosticToken(input.runId, createMissionSpineRunId(input.hop))!,
    timestamp: input.timestamp ?? Date.now(),
    hop: input.hop,
    status: input.status,
  };

  const namespace = sanitizeDiagnosticToken(input.namespace);
  const sourceId = sanitizeDiagnosticToken(input.sourceId);
  const reasonCode = sanitizeDiagnosticToken(input.reasonCode);
  const counts = sanitizeCounts(input.counts);
  const route = sanitizeRoute(input.route);

  if (namespace) marker.namespace = namespace;
  if (sourceId) marker.sourceId = sourceId;
  if (reasonCode) marker.reasonCode = reasonCode;
  if (counts) marker.counts = counts;
  if (route) marker.route = route;

  return marker;
}

export function installMissionSpineDebugCapture(): MissionSpineDebugState | null {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return null;

  if (!browserWindow.__OWNYOU_MISSION_SPINE__) {
    const markers: MissionSpineMarker[] = [];
    browserWindow.__OWNYOU_MISSION_SPINE__ = {
      markers,
      summarize: () => {
        const countsByHop: Record<string, number> = {};
        for (const marker of markers) {
          countsByHop[marker.hop] = (countsByHop[marker.hop] ?? 0) + 1;
        }

        return {
          markerCount: markers.length,
          hops: Object.keys(countsByHop).sort(),
          countsByHop,
          latest: markers.length > 0 ? markers[markers.length - 1] : undefined,
        };
      },
      reset: () => {
        markers.splice(0, markers.length);
        baselineEmitted = false;
      },
    };
  }

  return browserWindow.__OWNYOU_MISSION_SPINE__;
}

export function publishMissionSpineMarker(
  marker: MissionSpineMarker,
  opts: { log?: boolean; capture?: boolean; dispatch?: boolean } = {},
): MissionSpineMarker {
  const { log = true, capture = true, dispatch = true } = opts;
  const debugState = capture ? installMissionSpineDebugCapture() : null;
  if (debugState) {
    debugState.markers.push(marker);
    if (debugState.markers.length > MAX_MARKERS) {
      debugState.markers.splice(0, debugState.markers.length - MAX_MARKERS);
    }
  }

  if (dispatch) {
    const browserWindow = getBrowserWindow();
    if (browserWindow && typeof CustomEvent === 'function') {
      browserWindow.dispatchEvent(new CustomEvent(MISSION_SPINE_EVENT_NAME, { detail: marker }));
    }
  }

  if (log) {
    console.info(MISSION_SPINE_CONSOLE_PREFIX, marker);
  }

  return marker;
}

export function recordMissionSpineMarker(
  input: MissionSpineMarkerInput,
  opts?: { log?: boolean; capture?: boolean; dispatch?: boolean },
): MissionSpineMarker {
  return publishMissionSpineMarker(createMissionSpineMarker(input), opts);
}

export function emitMissionSpineBaseline(): MissionSpineMarker | null {
  if (baselineEmitted) return null;
  baselineEmitted = true;
  return recordMissionSpineMarker({
    runId: 'mission-spine-baseline',
    hop: 'baseline',
    status: 'baseline',
    reasonCode: 'capture-ready',
    counts: { markerSurface: 1 },
  });
}
